<template>
	<div class="flex">
		<div class="w-full max-w-4xl bg-white rounded-lg mx-4 px-6 overflow-y-auto max-h-[70vh]">
			<div
				class="sticky top-0 relative bg-white border-b border-ihh-slate-gray/20 text-ihh-midnight-gray text-xl sm:text-4xl font-semibold tracking-wide text-center flex w-full justify-center py-9"
			>
				{{ showTerms ? 'Terms and Conditions' : 'Write a Review' }}
			</div>

			<!-- Terms and Conditions Content -->
			<div v-if="showTerms" class="space-y-4 p-4 text-sm w-full text-start text-slate-500">
				<div class="font-semibold uppercase text-base text-ihh-slate-gray">Overview:</div>
				<div class="text-sm">
					By submitting a review on this website, you agree to the following terms and conditions:
					<div class="font-semibold uppercase text-base pt-2 text-ihh-slate-gray">Public Display:</div>
					<p>Your name and review may appear publicly for transparency.</p>
					<div class="font-semibold uppercase text-base pt-2 text-ihh-slate-gray">No Personal Info:</div>
					<p>We won't show emails, phone numbers, or sensitive data.</p>
					<div class="font-semibold uppercase text-base pt-2 text-ihh-slate-gray">Moderation:</div>
					<p>Reviews are moderated; we may approve, reject, or edit them.</p>
					<div class="font-semibold uppercase text-base pt-2 text-ihh-slate-gray">Ownership:</div>
					<p>Submitting a review grants us rights to its use without compensation.</p>
					<div class="font-semibold uppercase text-base pt-2 text-ihh-slate-gray">Accuracy:</div>
					<p>Reviews should be truthful; false info may result in rejection.</p>
					<div class="font-semibold uppercase text-base pt-2 text-ihh-slate-gray">Privacy:</div>
					<p>We value privacy; avoid sharing personal/sensitive data.</p>
					<div class="pt-4">Clicking submit confirms your agreement to these terms.</div>
					<br />Click to view our full
					<a href="/terms-conditions" class="text-center underline text-sky-700 hover:text-sky-500">Terms & Conditions.</a>
				</div>
				<div class="text-center pt-8">
					<button @click="showTerms = false" class="bg-ihh-dark-salmon hover:bg-ihh-salmon-400 px-4 py-3 font-semibold text-white rounded my-4">
						Back to Review
					</button>
				</div>
			</div>

			<!-- Review Form Content -->
			<div v-else class="flex flex-col md:flex-row w-full justify-center py-12 gap-y-8 md:gap-x-10">
				<div class="w-full md:max-w-sm">
					<div class="flex w-full justify-center md:justify-start items-center">
						<img :src="thumbnail" class="flex w-full max-w-[200px] mx-auto" />
					</div>
					<div class="font-semibold w-full text-center md:text-start pt-2">
						<div v-if="brand" class="text-sm text-ihh-gray">{{ brand }}</div>
						<div class="text-lg text-ihh-midnight-gray">{{ product.name }}</div>
					</div>
				</div>

				<div class="">
					<div class="font-semibold text-xl">
						<form @submit.prevent="onSubmit">
							<div class="space-y-8">
								<div class="w-full">
									<label for="rating" class="block text-ihh-slate-gray/90 text-xs sm:text-sm mb-px flex w-full"
										><span class="flex w-full justify-start text-start">Rating</span>
										<span class="flex w-full justify-end m-auto text-end uppercase text-xs sm:text-sm font-semibold text-ihh-slate-gray"
											>Required</span
										></label
									>
									<select
										id="rating"
										v-model="review.rating"
										class="focus:border-ihh-dark-salmon focus:outline-0 focus:shadow shadow-ihh-dark-salmon border border-gray-300 rounded px-4 py-3 flex w-full bg-white text-base text-ihh-slate-gray font-thin"
									>
										<option value="0" selected disabled class="deselect">Select Rating</option>
										<option value="1">1 star (Worse)</option>
										<option value="2">2 stars</option>
										<option value="3">3 stars (Average)</option>
										<option value="4">4 stars</option>
										<option value="5">5 stars (Best)</option>
									</select>
								</div>

								<div class="w-full">
									<label for="name" class="block text-ihh-slate-gray/90 text-xs sm:text-sm mb-px flex w-full"
										><span class="flex w-full justify-start text-start">Name</span>
										<span class="flex w-full justify-end m-auto text-end uppercase text-xs sm:text-sm font-semibold text-ihh-slate-gray"
											>Required</span
										></label
									>
									<input
										type="text"
										id="name"
										v-model="review.name"
										required
										class="focus:border-ihh-dark-salmon focus:outline-0 focus:shadow shadow-ihh-dark-salmon border border-gray-300 rounded px-4 sm:px-4 py-3 flex w-full text-sm bg-white font-normal"
									/>
								</div>

								<div class="w-full">
									<label for="email" class="block text-ihh-slate-gray/90 text-xs sm:text-sm mb-px flex w-full"
										><span class="flex w-full justify-start text-start">Email</span>
										<span class="flex w-full justify-end m-auto text-end uppercase text-xs sm:text-sm font-semibold text-ihh-slate-gray"
											>Required</span
										></label
									>
									<input
										type="text"
										id="email"
										v-model="review.email"
										required
										class="focus:border-ihh-dark-salmon focus:outline-0 focus:shadow shadow-ihh-dark-salmon border border-gray-300 rounded px-4 sm:px-4 py-3 flex w-full text-sm bg-white font-normal"
									/>
								</div>

								<div class="w-full">
									<label for="title" class="block text-ihh-slate-gray/90 text-xs sm:text-sm mb-px flex w-full"
										><span class="flex w-full justify-start text-start">Review Subject</span>
										<span class="flex w-full justify-end m-auto text-end uppercase text-xs sm:text-sm font-semibold text-ihh-slate-gray"
											>Required</span
										></label
									>
									<input
										type="text"
										id="title"
										v-model="review.title"
										required
										class="focus:border-ihh-dark-salmon focus:outline-0 focus:shadow shadow-ihh-dark-salmon border border-gray-300 rounded px-4 sm:px-4 py-3 flex w-full text-sm bg-white font-normal"
									/>
								</div>

								<div class="w-full mt-2">
									<label for="review" class="block text-ihh-slate-gray/90 text-xs sm:text-sm mb-px flex w-full"
										><span class="flex w-full justify-start text-start">Comments</span>
										<span class="flex w-full justify-end m-auto text-end uppercase text-xs sm:text-sm font-semibold text-ihh-slate-gray"
											>Required</span
										></label
									>

									<textarea
										type="text"
										id="review"
										v-model="review.text"
										required
										class="focus:border-ihh-dark-salmon focus:outline-0 focus:shadow shadow-ihh-dark-salmon border border-gray-300 rounded px-4 py-3 sm:py-4 flex w-full text-sm bg-white font-normal"
									/>
								</div>

								<div class="flex w-full my-8">
									<div class="relative flex items-start">
										<div class="flex h-5 items-center">
											<label
												class="custom-checkbox relative inline-block overflow-hidden w-4 h-4 bg-transparent rounded border border-gray-300 cursor-pointer mr-2"
											>
												<input
													required
													type="checkbox"
													id="agree"
													v-model="agree"
													value="agree"
													class="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
												/>
												<span class="checkmark absolute inset-0 hidden items-center justify-center">
													<svg class="w-3 h-3 text-white" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
														<path
															d="M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z"
														/>
													</svg>
												</span>
											</label>
										</div>
										<div class="ml-2 text-xs text-start font-normal capitalize">
											I agree to In His Hands
											<button @click.prevent="showTerms = true" class="underline capitalize text-sky-700 hover:text-sky-500">
												Terms and Conditions for Submitting Reviews.
											</button>
										</div>
									</div>
								</div>

								<div class="text-center flex w-full justify-center">
									<button
										type="submit"
										value="Submit"
										class="text-center text-sm sm:text-base text-ihh-midnight-gray w-full max-w-40 sm:max-w-60 py-3 rounded-lg bg-ihh-dark-salmon hover:bg-ihh-salmon duration-100 whitespace-nowrap font-semibold"
									>
										Submit Review
									</button>
								</div>
							</div>
						</form>
					</div>
				</div>
			</div>
			<div class="text-center"></div>
		</div>
	</div>
</template>

<script>
import Swal from 'sweetalert2'
import Mango from '../../../helpers/mango'
import Modal from '../../layout/modal.vue'
import Logo from '../../partials/logo.vue'
export default {
	components: { Logo, Modal },
	inject: ['store', 'axios'],
	props: ['product'],
	data() {
		return {
			closeForm: false,
			showTerms: false,
			review: {
				title: '',
				text: '',
				status: 'pending',
				rating: 0,
				email: this.store.user?.email || '',
				name: this.store.user?.firstName ? `${this.store.user?.firstName} ${this.store.user?.lastName}` : '',
				date_reviewed: '',
				productBcId: this.product.bcId,
				productId: this.product.id,
			},
			terms: false,
			brand: null,
		}
	},

	mounted() {
		this.fetchBrand(this.product.brandId)

		const date = new Date()
		this.review.date_reviewed = date.toISOString()
	},

	methods: {
		emitCloseReviewForm() {
			this.$emit('close-review', false) // Emit an event to the parent component with 'false' as payload
		},
		async fetchBrand(id) {
			if (!id) return
			const brand = await Mango.brands({ search: { id } })
			this.brand = brand[0]?.name
		},
		async onSubmit() {
			if (this.review.name === '' || this.review.title === '' || this.review.text === '' || this.review.rating === null) {
				alert('Review is incomplete! Please fill out every field.')
				return
			}

			let response = await this.axios.post(`${this.store.api}/controllers/bigCommerce/reviews`, this.review) // Save the review to the database

			console.log('Response: ', response)

			if (response?.data?.id) {
				Swal.fire({
					title: 'Review Submitted',
					text: 'Thank you for your review!',
					icon: 'success',
					confirmButtonColor: '#e59789',
				})

				this.emitCloseReviewForm()
			} else {
				Swal.fire({
					title: 'Review Submission Failed',
					text: 'There was an error submitting your review. Please try again later.',
					icon: 'error',
					confirmButtonColor: '#e59789',
				})
			}

			this.$emit('review-submitted', this.review) // Emitting the event to the parent
			this.$emit('close-form') // Emitting another event to close the form

			this.review = {
				title: '',
				text: '',
				status: 'pending',
				rating: 0,
				email: this.store.user?.email || '',
				name: this.store.user?.firstName ? `${this.store.user?.firstName} ${this.store.user?.lastName}` : '',
				date_reviewed: '',
				productBcId: this.product.bcId,
				productId: this.product.id,
			}
		},
	},

	computed: {
		thumbnail() {
			return this.product.images.find((img) => img.isThumbnail)?.url
		},
	},
}
</script>

<style>
.custom-checkbox input[type='checkbox']:checked ~ .checkmark {
	display: flex;
	background-color: var(--ihh-dark-salmon);
	border: 0;
}
</style>
